import { filter, identity, isEmpty, map, noop, size } from 'underscore';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ActionButton, IconButton, Label, Stack, TextField, getId } from '@fluentui/react';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { UrlTestTool } from '@bingads-webui-campaign/url-test-tool';
import { Error } from './error';
import {
  validateTrackingUrlTemplate,
  validateTrackingUrlTemplateStringOnly,
  validateFinalUrlSuffix,
  validateUrlCustomParameters,
  checkAdFieldLengthLimit,
} from './validators';
import { legacyStrings } from './legacy-i18n';
import { styles as defaultStyles } from './styles';

const MAX_CUSTOM_PARAMETERS_COUNT = 8;
const TRACKING_URL_TEMPLATE = 'TrackingUrlTemplate';
const FINAL_URL_SUFFIX = 'FinalUrlSuffix';
const URL_CUSTOM_PARAMETERS = 'UrlCustomParameters';
const URL_CUSTOM_PARAMETERS_KEY = 'UrlCustomParametersKey';
const URL_CUSTOM_PARAMETERS_VALUE = 'UrlCustomParametersValue';
const Validators = {
  [TRACKING_URL_TEMPLATE]: validateTrackingUrlTemplate,
  [FINAL_URL_SUFFIX]: validateFinalUrlSuffix,
  [URL_CUSTOM_PARAMETERS]: validateUrlCustomParameters,
};
const ValidatorsStringOnly = {
  [TRACKING_URL_TEMPLATE]: validateTrackingUrlTemplateStringOnly,
};

const TrackingUrlTemplateControls = ({
  i18n, id, value, onChange, formErrors,
}) => (
  <>
    <TextField
      id={id}
      value={value}
      onChange={onChange}
    />
    <div>{i18n.getString(_TL_('Example: http://www.trackingtool.com/tracking?url={lpurl}&type=1'))}</div>
    <Error errors={formErrors} property={TRACKING_URL_TEMPLATE} />
  </>
);

TrackingUrlTemplateControls.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  formErrors: PropTypes.arrayOf(PropTypes.shape({})),
};

TrackingUrlTemplateControls.defaultProps = {
  id: undefined,
  value: '',
  onChange: undefined,
  formErrors: [],
};

const FinalUrlSuffixControls = ({
  i18n, id, value, onChange, formErrors,
}) => (
  <>
    <TextField
      id={id}
      value={value}
      onChange={onChange}
    />
    <div>{i18n.getString(_TL_('Example: param1=values1&param2=value2'))}</div>
    <Error errors={formErrors} property={FINAL_URL_SUFFIX} />
  </>
);

FinalUrlSuffixControls.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  formErrors: PropTypes.arrayOf(PropTypes.shape({})),
};

FinalUrlSuffixControls.defaultProps = {
  id: undefined,
  value: '',
  onChange: undefined,
  formErrors: [],
};

const UrlCustomParametersControls = ({
  i18n, customParameters, onCustomParameterKeyChange, onCustomParameterValueChange, onRemoveUrlParameter, onAddUrlParameter, formErrors,
}) => (
  <>
    <Stack
      className="custom-parameters"
      tokens={{ childrenGap: 6 }}
    >
      {map(customParameters, ({ Key, Value }, index) => (
        <div className="custom-parameters-entry">
          <div key={`left-label-${index}`} className="left-label">{'{ _'}</div>
          <TextField
            key={`left-input-${index}`}
            className="left-input"
            ariaLabel={i18n.getString(_TL_('Custom parameters key {{index}}'), { index: index + 1 })}
            value={Key}
            onChange={(event, value) => onCustomParameterKeyChange(index, value)}
          />
          <div key={index} className="right-label">{'} ='}</div>
          <TextField
            key={`right-input-${index}`}
            className="right-input"
            ariaLabel={i18n.getString(_TL_('Custom parameters value {{index}}'), { index: index + 1 })}
            value={Value}
            onChange={(event, value) => onCustomParameterValueChange(index, value)}
          />
          {index !== 0 && <IconButton
            key={`remove-url-parameter-${index}`}
            className="remove-url-parameter"
            iconProps={{ iconName: 'Cancel' }}
            ariaLabel={i18n.getString(_TL_('Remove custom parameters number {{index}}'), { index })}
            onClick={() => onRemoveUrlParameter(index)}
          />}
          <Error
            key={`errors-${index}`}
            errors={formErrors}
            property={URL_CUSTOM_PARAMETERS}
            index={index}
          />
        </div>
      ))}
    </Stack>
    {size(customParameters) < MAX_CUSTOM_PARAMETERS_COUNT &&
      <ActionButton
        className="add-url-parameter"
        onClick={onAddUrlParameter}
      >
        {i18n.getString(_TL_('Add another'))}
      </ActionButton>
    }
    <Error errors={formErrors} property={URL_CUSTOM_PARAMETERS} index={-1} />
  </>
);

UrlCustomParametersControls.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onCustomParameterKeyChange: PropTypes.func.isRequired,
  onCustomParameterValueChange: PropTypes.func.isRequired,
  onRemoveUrlParameter: PropTypes.func.isRequired,
  onAddUrlParameter: PropTypes.func.isRequired,
  customParameters: PropTypes.arrayOf(PropTypes.shape({})),
  formErrors: PropTypes.arrayOf(PropTypes.shape({})),
};

UrlCustomParametersControls.defaultProps = {
  customParameters: [],
  formErrors: [],
};

export const UrlOptionsParameters = ({
  i18n, styles, instrumentation, errors, enableSelfValidation, disableLengthConstraints, trackingUrlTemplate, finalUrlSuffix, urlCustomParameters,
  onChange, showFinalUrlSuffix, showCustomParameters, showUurlTestTool, token, triggerTestUurl, isControlledOutside,
  trackingUrlTemplateControlsWrapper, finalUrlSuffixControlsWrapper, urlCustomParametersControlsWrapper, isTrackingUrlTemplateRequired, isWithError,
}) => {
  const legacyI18n = useMemo(
    () => buildLegacyI18n(i18n, [
      legacyStrings,
    ]),
    [i18n]
  );

  const trackingUrlTemplateId = useMemo(() => getId(TRACKING_URL_TEMPLATE), []);
  const finalUrlSuffixId = useMemo(() => getId(FINAL_URL_SUFFIX), []);

  const [cachedTrackingUrlTemplate, setCachedTrackingUrlTemplate] = useState(trackingUrlTemplate);
  const [cachedFinalUrlSuffix, setCachedFinaUrlSuffix] = useState(finalUrlSuffix);
  const [cachedCustomeParameters, setCachedCustomParameters] = useState(urlCustomParameters);

  const [formErrors, setFormErrors] = useState(errors);
  useEffect(() => {
    setFormErrors(errors);
  }, [errors]);

  const onValidate = useCallback((value, key) => {
    let newErrors = [];
    if (enableSelfValidation) {
      newErrors = filter(formErrors, error => error.Property !== key);

      if (key === TRACKING_URL_TEMPLATE) {
        const errorsArray = isWithError ? ValidatorsStringOnly[key](value, i18n, isTrackingUrlTemplateRequired) : Validators[key](value, i18n);
        newErrors.push(...errorsArray);
      } else {
        newErrors.push(...Validators[key](value, i18n));
      }

      if (!isWithError) setFormErrors(newErrors);
    }
    onChange(value, key, newErrors);
  }, [enableSelfValidation, formErrors, i18n, isTrackingUrlTemplateRequired, onChange, isWithError]);

  const onTrackingUrlTemplateChange = useCallback((event, value) => {
    if (disableLengthConstraints || checkAdFieldLengthLimit(value, TRACKING_URL_TEMPLATE)) {
      setCachedTrackingUrlTemplate(value);
      onValidate(value, TRACKING_URL_TEMPLATE);
    }
  }, [onValidate, disableLengthConstraints]);

  const onFinalUrlSuffixChange = useCallback((event, value) => {
    if (disableLengthConstraints || checkAdFieldLengthLimit(value, FINAL_URL_SUFFIX)) {
      setCachedFinaUrlSuffix(value);
      onValidate(value, FINAL_URL_SUFFIX);
    }
  }, [onValidate, disableLengthConstraints]);

  const onCustomParameterKeyChange = useCallback((index, key) => {
    if (disableLengthConstraints || checkAdFieldLengthLimit(key, URL_CUSTOM_PARAMETERS_KEY)) {
      const customParameters = cloneDeep(isControlledOutside ? urlCustomParameters : cachedCustomeParameters);
      customParameters[index].Key = key;
      setCachedCustomParameters(customParameters);
      onValidate(customParameters, URL_CUSTOM_PARAMETERS);
    }
  }, [disableLengthConstraints, isControlledOutside, urlCustomParameters, cachedCustomeParameters, onValidate]);

  const onCustomParameterValueChange = useCallback((index, value) => {
    if (disableLengthConstraints || checkAdFieldLengthLimit(value, URL_CUSTOM_PARAMETERS_VALUE)) {
      const customParameters = cloneDeep(isControlledOutside ? urlCustomParameters : cachedCustomeParameters);
      customParameters[index].Value = value;
      setCachedCustomParameters(customParameters);
      onValidate(customParameters, URL_CUSTOM_PARAMETERS);
    }
  }, [disableLengthConstraints, isControlledOutside, urlCustomParameters, cachedCustomeParameters, onValidate]);

  const onAddUrlParameter = useCallback(() => {
    const customParameters = [
      ...cloneDeep(isControlledOutside ? urlCustomParameters : cachedCustomeParameters),
      { Key: '', Value: '' },
    ];
    setCachedCustomParameters(customParameters);
    onValidate(customParameters, URL_CUSTOM_PARAMETERS);
  }, [cachedCustomeParameters, isControlledOutside, onValidate, urlCustomParameters]);

  const onRemoveUrlParameter = useCallback((index) => {
    const customParameters = cloneDeep(isControlledOutside ? urlCustomParameters : cachedCustomeParameters);
    customParameters.splice(index, 1);
    setCachedCustomParameters(customParameters);
    onValidate(customParameters, URL_CUSTOM_PARAMETERS);
  }, [cachedCustomeParameters, isControlledOutside, onValidate, urlCustomParameters]);

  useEffect(() => {
    if (isEmpty(cachedCustomeParameters)) {
      setCachedCustomParameters([{ Key: '', Value: '' }]);
    }
  }, [cachedCustomeParameters, urlCustomParameters]);

  useEffect(() => {
    if (isControlledOutside && isEmpty(urlCustomParameters)) {
      onValidate([{ Key: '', Value: '' }], URL_CUSTOM_PARAMETERS);
    }
  }, [isControlledOutside, onValidate, urlCustomParameters]);


  const urlTestTool = useMemo(() => new UrlTestTool({
    token,
    triggerTestUurl,
    instrumentation,
    i18n: legacyI18n,
  }), [legacyI18n, instrumentation, token, triggerTestUurl]);

  const urlTestToolContainerRef = useCallback((node) => {
    if (node !== null) {
      node.append(urlTestTool.render().el);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const TrackingUrlTemplateControlsWrapped = useMemo(() => trackingUrlTemplateControlsWrapper(TrackingUrlTemplateControls), [trackingUrlTemplateControlsWrapper]);
  const FinalUrlSuffixControlsWrapped = useMemo(() => finalUrlSuffixControlsWrapper(FinalUrlSuffixControls), [finalUrlSuffixControlsWrapper]);
  const UrlCustomParametersControlsWrapped = useMemo(() => urlCustomParametersControlsWrapper(UrlCustomParametersControls), [urlCustomParametersControlsWrapper]);

  return (
    <Stack className="url-options-parameters" tokens={{ childrenGap: 16 }} styles={styles} >
      <Stack className="tracking-template">
        <Label htmlFor={trackingUrlTemplateId}>
          <Stack horizontal>
            {i18n.getString(_TL_('Tracking template'))}
            <HelpPopupInline topic="pop_BA_NewCampaignCreate_Ad_TrackTemplate" />
          </Stack>
        </Label>
        <TrackingUrlTemplateControlsWrapped
          i18n={i18n}
          id={trackingUrlTemplateId}
          value={isControlledOutside ? trackingUrlTemplate : cachedTrackingUrlTemplate}
          onChange={onTrackingUrlTemplateChange}
          formErrors={formErrors}
        />
      </Stack>

      {showFinalUrlSuffix &&
        <Stack className="final-url-suffix">
          <Label htmlFor={finalUrlSuffixId}>
            <Stack horizontal>
              {i18n.getString(_TL_('Final URL suffix'))}
              <HelpPopupInline topic="pop_BA_NewAdCreate_FinalURLSuffix" />
            </Stack>
          </Label>
          <FinalUrlSuffixControlsWrapped
            i18n={i18n}
            id={finalUrlSuffixId}
            value={isControlledOutside ? finalUrlSuffix : cachedFinalUrlSuffix}
            onChange={onFinalUrlSuffixChange}
            formErrors={formErrors}
          />
        </Stack>}
      {
        showCustomParameters &&
        <Stack className="custom-parameters-controls">
          <Label>
            <Stack horizontal>
              {i18n.getString(_TL_('Custom parameters'))}
              <HelpPopupInline topic="pop_BA_NewCampaignCreate_Ad_Parameters" />
            </Stack>
          </Label>
          <UrlCustomParametersControlsWrapped
            i18n={i18n}
            customParameters={isControlledOutside ? urlCustomParameters : cachedCustomeParameters}
            onCustomParameterKeyChange={onCustomParameterKeyChange}
            onCustomParameterValueChange={onCustomParameterValueChange}
            onRemoveUrlParameter={onRemoveUrlParameter}
            onAddUrlParameter={onAddUrlParameter}
            formErrors={formErrors}
          />
        </Stack>
      }
      {
        showUurlTestTool ?
          <Stack>
            <div className="uurl-test-tool__container" ref={urlTestToolContainerRef} />
          </Stack> : null
      }
    </Stack>
  );
};

UrlOptionsParameters.propTypes = {
  styles: PropTypes.shape({}),

  trackingUrlTemplate: PropTypes.string,

  finalUrlSuffix: PropTypes.string,

  urlCustomParameters: PropTypes.arrayOf(PropTypes.shape({
    Key: PropTypes.string,
    Value: PropTypes.string,
  })),

  errors: PropTypes.arrayOf(PropTypes.shape({
    Code: PropTypes.string,
    Message: PropTypes.string,
    Property: PropTypes.string,
    Index: PropTypes.number,
  })),

  enableSelfValidation: PropTypes.bool,
  disableLengthConstraints: PropTypes.bool,

  onChange: PropTypes.func,

  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,

  showUurlTestTool: PropTypes.bool,

  showFinalUrlSuffix: PropTypes.bool,

  showCustomParameters: PropTypes.bool,

  token: PropTypes.string,

  triggerTestUurl: PropTypes.func,

  instrumentation: PropTypes.shape({}),

  trackingUrlTemplateControlsWrapper: PropTypes.func,
  finalUrlSuffixControlsWrapper: PropTypes.func,
  urlCustomParametersControlsWrapper: PropTypes.func,

  isControlledOutside: PropTypes.bool,

  isTrackingUrlTemplateRequired: PropTypes.bool.isRequired,

  isWithError: PropTypes.bool.isRequired,
};

UrlOptionsParameters.defaultProps = {
  styles: defaultStyles,
  trackingUrlTemplate: '',
  finalUrlSuffix: '',
  urlCustomParameters: [],
  errors: [],
  enableSelfValidation: false,
  disableLengthConstraints: false,
  onChange: noop,
  showUurlTestTool: false,
  showFinalUrlSuffix: false,
  showCustomParameters: true,
  token: '',
  triggerTestUurl: noop,
  instrumentation: {},
  trackingUrlTemplateControlsWrapper: identity,
  finalUrlSuffixControlsWrapper: identity,
  urlCustomParametersControlsWrapper: identity,
  isControlledOutside: false,
};
